import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useData } from '../../context/DataContext';
import { notificationsAPI } from '../../services/api';
import Modal from '../../components/Modal';
import { includesNormalized } from '../../utils/searchUtils';
import { formatUserDisplayName } from '../../utils/userDisplayName';
import './Notifications.css';

const AdminNotifications = () => {
  const { t } = useTranslation();
  const { students, instructors, cohorts } = useData();
  const [items, setItems] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [health, setHealth] = useState(null);
  const [bulkResult, setBulkResult] = useState(null);
  const [query, setQuery] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [channelFilter, setChannelFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [deliveryFilter, setDeliveryFilter] = useState('all');
  const [form, setForm] = useState({ targetType: 'individual', recipientId: '', cohortId: '', title: '', message: '', type: 'system', channels: ['portal', 'email'] });

  const resetForm = () => setForm({ targetType: 'individual', recipientId: '', cohortId: '', title: '', message: '', type: 'system', channels: ['portal', 'email'] });

  const recipients = [...students, ...instructors]
    .map((r) => ({
      ...r,
      displayName: formatUserDisplayName(r)
    }))
    .sort((a, b) => String(a.displayName).localeCompare(String(b.displayName), 'nl'));

  const loadItems = async () => {
    setLoading(true);
    try {
      const response = await notificationsAPI.getAll({ limit: 200 });
      setItems(response.notifications || []);
    } finally {
      setLoading(false);
    }
  };

  const loadHealth = async () => {
    try {
      const response = await notificationsAPI.getDeliveryHealth();
      setHealth(response);
    } catch (error) {
      setHealth(null);
    }
  };

  useEffect(() => {
    loadItems();
    loadHealth();
  }, []);

  const getDeliveryStatus = (item) => {
    const wantsWhatsApp = (item.channels || []).includes('whatsapp');
    const wantsEmail = (item.channels || []).includes('email') || Boolean(item.meta?.email);
    const whatsappFailed = wantsWhatsApp && item.whatsappStatus === 'failed';
    const emailFailed = wantsEmail && item.meta?.email?.status === 'failed';

    if (whatsappFailed || emailFailed) return 'failed';

    const whatsappSent = !wantsWhatsApp || item.whatsappStatus === 'sent';
    const emailSent = !wantsEmail || item.meta?.email?.status === 'sent';
    if (whatsappSent && emailSent) return 'sent';

    if ((wantsWhatsApp && item.whatsappStatus === 'queued') || (wantsEmail && item.meta?.email?.status === 'queued')) {
      return 'queued';
    }

    return 'other';
  };

  const canRetryDelivery = (item) => {
    const status = getDeliveryStatus(item);
    return status === 'failed' || status === 'queued';
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      if (form.targetType === 'cohort') {
        const result = await notificationsAPI.createBulk({
          cohortId: form.cohortId,
          title: form.title,
          message: form.message,
          type: form.type,
          channels: form.channels
        });
        setBulkResult(result);
      } else {
        await notificationsAPI.create({
          recipientId: form.recipientId,
          title: form.title,
          message: form.message,
          type: form.type,
          channels: form.channels
        });
      }
      resetForm();
      setShowModal(false);
      await loadItems();
    } catch (error) {
      alert(error.message || 'Melding aanmaken mislukt');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Weet u zeker dat u deze melding wilt verwijderen?')) return;
    try {
      await notificationsAPI.delete(id);
      setItems((prev) => prev.filter((item) => item.id !== id));
    } catch (error) {
      alert(error.message || 'Verwijderen mislukt');
    }
  };

  const handleProcessQueue = async () => {
    setProcessing(true);
    try {
      const result = await notificationsAPI.processQueue(50);
      alert(`Queue verwerkt: ${result.processedCount}`);
      await loadItems();
      await loadHealth();
    } catch (error) {
      alert(error.message || 'Queue verwerken mislukt');
    } finally {
      setProcessing(false);
    }
  };

  const handleRetryDelivery = async (itemId) => {
    try {
      await notificationsAPI.retryDelivery(itemId);
      await loadItems();
      await loadHealth();
    } catch (error) {
      alert(error.message || 'Opnieuw verzenden mislukt');
    }
  };

  const availableTypes = useMemo(() => {
    return Array.from(new Set(items.map((item) => item.type).filter(Boolean))).sort((a, b) => a.localeCompare(b, 'nl'));
  }, [items]);

  const filteredItems = useMemo(() => {
    return items.filter((item) => {
      if (typeFilter !== 'all' && item.type !== typeFilter) return false;
      if (channelFilter !== 'all' && !(item.channels || []).includes(channelFilter)) return false;
      if (statusFilter === 'read' && !item.isRead) return false;
      if (statusFilter === 'unread' && item.isRead) return false;
      if (deliveryFilter !== 'all' && getDeliveryStatus(item) !== deliveryFilter) return false;

      if (query) {
        const haystack = [item.title, item.message, item.recipient?.name, item.type, item.channels?.join(' '), item.whatsappStatus].join(' ');
        if (!includesNormalized(haystack, query)) return false;
      }

      return true;
    });
  }, [items, typeFilter, channelFilter, statusFilter, deliveryFilter, query]);

  const formatDateTime = (value) => {
    if (!value) return '-';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '-';
    return date.toLocaleString('nl-NL', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="users-page admin-notifications-page">
      <div className="page-header">
        <h1>{t('notification.title')}</h1>
        <div className="form-actions">
          <button className="btn-secondary" onClick={handleProcessQueue} disabled={processing}>
            {processing ? 'Processing...' : 'Process WhatsApp Queue'}
          </button>
          <button className="btn-primary" onClick={() => { resetForm(); setShowModal(true); }}>{t('common.add')}</button>
        </div>
      </div>

      {bulkResult && (
        <div className="notification-bulk-result">
          <span>
            <strong>Cohort mailing verzonden:</strong> {bulkResult.sent} van {bulkResult.total} studenten bereikt in cohort &quot;{bulkResult.cohort}&quot;.
            {bulkResult.failed > 0 && ` (${bulkResult.failed} mislukt)`}
          </span>
          <button type="button" onClick={() => setBulkResult(null)}>✕</button>
        </div>
      )}

      {health && (
        <div className="notification-health-grid">
          <article className={`notification-health-card ${health.email?.verified ? 'ok' : 'warn'}`}>
            <h3>E-mail service</h3>
            <p>Status: {health.email?.verified ? 'Actief' : (health.email?.configured ? 'Configuratie fout' : 'Niet geconfigureerd')}</p>
            <small>{health.email?.error || `Afzender: ${health.email?.from || '-'}`}</small>
          </article>
          <article className={`notification-health-card ${health.whatsapp?.configured ? 'ok' : 'warn'}`}>
            <h3>WhatsApp service</h3>
            <p>Status: {health.whatsapp?.configured ? 'Actief' : 'Niet geconfigureerd'}</p>
            <small>Queue: {health.whatsapp?.queued || 0}, Failed: {health.whatsapp?.failed || 0}</small>
          </article>
          <article className="notification-health-card neutral">
            <h3>Portal meldingen</h3>
            <p>Ongelezen: {health.portal?.unread || 0}</p>
            <small>Laatste update: {new Date().toLocaleTimeString('nl-NL')}</small>
          </article>
        </div>
      )}

      <div className="notification-filter-row">
        <input
          type="search"
          className="form-input"
          placeholder="Zoek op titel, ontvanger of bericht"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
        />
        <select className="form-input" value={typeFilter} onChange={(event) => setTypeFilter(event.target.value)}>
          <option value="all">Alle types</option>
          {availableTypes.map((type) => (
            <option key={type} value={type}>{type}</option>
          ))}
        </select>
        <select className="form-input" value={channelFilter} onChange={(event) => setChannelFilter(event.target.value)}>
          <option value="all">Alle kanalen</option>
          <option value="portal">Portal</option>
          <option value="whatsapp">WhatsApp</option>
          <option value="email">E-mail</option>
        </select>
        <select className="form-input" value={statusFilter} onChange={(event) => setStatusFilter(event.target.value)}>
          <option value="all">Leesstatus: alles</option>
          <option value="unread">Alleen ongelezen</option>
          <option value="read">Alleen gelezen</option>
        </select>
        <select className="form-input" value={deliveryFilter} onChange={(event) => setDeliveryFilter(event.target.value)}>
          <option value="all">Verzending: alles</option>
          <option value="sent">Alleen verzonden</option>
          <option value="queued">Alleen in wachtrij</option>
          <option value="failed">Alleen mislukt</option>
        </select>
      </div>

      <div className="notification-list-grid">
        {loading && <p>Meldingen laden...</p>}
        {!loading && filteredItems.length === 0 && <p>Geen meldingen gevonden voor deze filters.</p>}
        {!loading && filteredItems.map((item) => (
          <article key={item.id} className="notification-list-card">
            <div className="notification-list-head">
              <h3>{item.title}</h3>
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                <span className={`notification-status-pill ${item.isRead ? 'read' : 'unread'}`}>
                  {item.isRead ? 'Gelezen' : 'Ongelezen'}
                </span>
                <button type="button" className="btn-danger-sm" onClick={() => handleDelete(item.id)}>Verwijder</button>
              </div>
            </div>
            <p>{item.message}</p>
            <div className="notification-list-meta">
              <span><strong>Ontvanger:</strong> {item.recipient?.name || '-'}</span>
              <span><strong>Type:</strong> {item.type}</span>
              <span><strong>Kanalen:</strong> {(item.channels || []).join(', ') || '-'}</span>
              <span><strong>WhatsApp:</strong> {item.whatsappStatus || '-'}</span>
              <span><strong>E-mail:</strong> {item.meta?.email?.status || '-'}</span>
              <span><strong>Delivery:</strong> {getDeliveryStatus(item)}</span>
              <span><strong>Aangemaakt:</strong> {formatDateTime(item.createdAt)}</span>
            </div>
            {canRetryDelivery(item) && (
              <div className="form-actions" style={{ marginTop: '8px' }}>
                <button type="button" className="btn-secondary" onClick={() => handleRetryDelivery(item.id)}>
                  Opnieuw verzenden
                </button>
              </div>
            )}
          </article>
        ))}
      </div>

      <Modal isOpen={showModal} onClose={() => { setShowModal(false); resetForm(); }} title="Nieuwe melding">
        <form onSubmit={handleCreate} className="add-form">

          <div className="notification-target-toggle">
            <button
              type="button"
              className={form.targetType === 'individual' ? 'active' : ''}
              onClick={() => setForm((prev) => ({ ...prev, targetType: 'individual', cohortId: '' }))}
            >
              Individueel
            </button>
            <button
              type="button"
              className={form.targetType === 'cohort' ? 'active' : ''}
              onClick={() => setForm((prev) => ({ ...prev, targetType: 'cohort', recipientId: '' }))}
            >
              Cohort
            </button>
          </div>

          {form.targetType === 'individual' ? (
            <select
              className="form-input"
              value={form.recipientId}
              onChange={(e) => setForm((prev) => ({ ...prev, recipientId: e.target.value }))}
              required
            >
              <option value="">Selecteer ontvanger</option>
              {recipients.map((r) => (
                <option key={r.id} value={r.id}>{r.displayName}</option>
              ))}
            </select>
          ) : (
            <select
              className="form-input"
              value={form.cohortId}
              onChange={(e) => setForm((prev) => ({ ...prev, cohortId: e.target.value }))}
              required
            >
              <option value="">Selecteer cohort</option>
              {cohorts.map((c) => (
                <option key={c.id} value={c.id}>{c.name} — {c.location}</option>
              ))}
            </select>
          )}

          <input
            className="form-input"
            value={form.title}
            onChange={(e) => setForm((prev) => ({ ...prev, title: e.target.value }))}
            placeholder="Titel"
            required
          />
          <textarea
            className="form-textarea"
            value={form.message}
            onChange={(e) => setForm((prev) => ({ ...prev, message: e.target.value }))}
            placeholder="Bericht"
            rows={4}
            required
          />
          <div className="form-row">
            <label>
              <input
                type="checkbox"
                checked={form.channels.includes('portal')}
                onChange={(e) => setForm((prev) => ({
                  ...prev,
                  channels: e.target.checked
                    ? Array.from(new Set([...prev.channels, 'portal']))
                    : prev.channels.filter((c) => c !== 'portal')
                }))}
              />
              Portal
            </label>
            <label>
              <input
                type="checkbox"
                checked={form.channels.includes('email')}
                onChange={(e) => setForm((prev) => ({
                  ...prev,
                  channels: e.target.checked
                    ? Array.from(new Set([...prev.channels, 'email']))
                    : prev.channels.filter((c) => c !== 'email')
                }))}
              />
              E-mail
            </label>
            <label>
              <input
                type="checkbox"
                checked={form.channels.includes('whatsapp')}
                onChange={(e) => setForm((prev) => ({
                  ...prev,
                  channels: e.target.checked
                    ? Array.from(new Set([...prev.channels, 'whatsapp']))
                    : prev.channels.filter((c) => c !== 'whatsapp')
                }))}
              />
              WhatsApp
            </label>
          </div>
          <div className="form-actions">
            <button className="btn-primary" type="submit">Verzenden</button>
            <button className="btn-secondary" type="button" onClick={() => { setShowModal(false); resetForm(); }}>{t('common.cancel')}</button>
          </div>
        </form>
      </Modal>
    </div>
  );
};

export default AdminNotifications;
